import { Box } from '@mui/material';
import { Selector } from 'components/Form/Selectors/Selector';
import useAppTheme from 'hooks/useAppTheme';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { getAssetMakeModel } from 'views/FleetOverview/components/AssetCardsV2/utils';
import { StyledBox, StyledTypography } from './SensorAnalysis.styles';
import { composeUniqueKey } from 'utils/helpers/string.manipulation';
import {
  addLiveDataToAssets,
  isAssetIgnition,
} from 'views/FleetOverview/helpers/asset.helpers';
import type { Asset } from 'types/asset.types';
import {
  setSelectedAsset,
  setSelectedMakeModel,
} from 'store/sensorAnalysis.slice';
import { useLocation } from 'react-router-dom';
import { findAssetWithMaxValue } from 'utils/assets';
import { useTranslation } from 'react-i18next';

const AssetSelector = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const initRef = useRef(false);

  const storageData = useMemo(() => {
    try {
      const data = localStorage.getItem('sensorAnalysisMakeModel');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Failed to parse localStorage data:', e);
      return null;
    }
  }, []);

  const makeModelReceived = location.state?.makemodel ?? storageData?.makeModel;

  const assets = useAppSelector((state) => state.assetReducer.assets);
  const dispatch = useAppDispatch();
  const { enableDarkTheme } = useAppTheme();
  const latestTagData = useAppSelector(
    (state) => state.deviceReducer.latestTagData
  );

  const selectedAsset = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedAsset
  );

  const selectedMakeModel = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedMakeModel
  );

  const handleSelectAsset = useCallback(
    (bumperNumber: string) => {
      const item = assets.find((asset) => asset.bumperNumber === bumperNumber);
      if (item) {
        dispatch(setSelectedAsset(item));
      }
    },
    [assets, dispatch]
  );

  const assetsWithLiveData = useMemo(
    () => addLiveDataToAssets(assets, latestTagData),
    [assets, latestTagData]
  );

  const makeModels = useMemo(() => getAssetMakeModel(assets), [assets]);

  const makeModelList = useMemo(
    () =>
      makeModels.map((item) => {
        return {
          id: item,
          display: item,
        };
      }),
    [makeModels]
  );

  const assetBumperIds = useMemo(() => {
    return assets
      .filter(
        (asset) =>
          composeUniqueKey([asset.make, asset.model]) ===
          selectedMakeModel.replace('-', '!')
      )
      .map((item) => item.bumperNumber)
      .sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
  }, [assets, selectedMakeModel]);

  const assetList = useMemo(
    () =>
      assetBumperIds.map((item) => {
        return {
          id: item,
          display: item,
          bumperId: true,
          isActive: latestTagData
            ? isAssetIgnition(
                latestTagData,
                assetsWithLiveData.find(
                  (asset) => asset.bumperNumber === item
                ) as Asset
              )
            : undefined,
        };
      }),
    [assetBumperIds, latestTagData, assetsWithLiveData]
  );

  useEffect(() => {
    if (initRef.current || assets.length === 0) return;

    let makeModelToSet = '';

    if (makeModelReceived?.make && makeModelReceived?.model) {
      makeModelToSet = `${makeModelReceived.make.toLowerCase()}-${makeModelReceived.model.toLowerCase()}`;
      dispatch(setSelectedMakeModel(makeModelToSet));
    } else if (makeModels.length > 0) {
      makeModelToSet = makeModels[0];
      dispatch(setSelectedMakeModel(makeModelToSet));
    }

    if (makeModelToSet) {
      const matchingAssets = assets.filter(
        (asset) =>
          composeUniqueKey([asset.make, asset.model]) ===
          makeModelToSet.replace('-', '!')
      );

      if (matchingAssets.length > 0) {
        if (makeModelReceived?.bumperNumber) {
          const specificAsset = matchingAssets.find(
            (asset) => asset.bumperNumber === makeModelReceived.bumperNumber
          );
          if (specificAsset) {
            dispatch(setSelectedAsset(specificAsset));
          } else {
            dispatch(
              setSelectedAsset(
                findAssetWithMaxValue(matchingAssets) ?? matchingAssets[0]
              )
            );
          }
        } else {
          dispatch(
            setSelectedAsset(
              findAssetWithMaxValue(matchingAssets) ?? matchingAssets[0]
            )
          );
        }
      }
    }

    initRef.current = true;
  }, [assets, makeModels, makeModelReceived, dispatch]);

  useEffect(() => {
    if (!initRef.current || !selectedMakeModel || assets.length === 0) return;

    const matchingAssets = assets.filter(
      (asset) =>
        composeUniqueKey([asset.make, asset.model]) ===
        selectedMakeModel.replace('-', '!')
    );

    const currentAssetMatchesMakeModel =
      selectedAsset &&
      composeUniqueKey([selectedAsset.make, selectedAsset.model]) ===
        selectedMakeModel.replace('-', '!');

    if (!currentAssetMatchesMakeModel && matchingAssets.length > 0) {
      dispatch(
        setSelectedAsset(
          findAssetWithMaxValue(matchingAssets) ?? matchingAssets[0]
        )
      );
    }
  }, [selectedMakeModel, assets, selectedAsset, dispatch]);

  useEffect(() => {
    if (
      selectedAsset?.make &&
      selectedAsset?.model &&
      selectedAsset?.bumperNumber
    ) {
      const data = {
        makeModel: {
          make: selectedAsset.make,
          model: selectedAsset.model,
          bumperNumber: selectedAsset.bumperNumber,
        },
      };

      localStorage.setItem('sensorAnalysisMakeModel', JSON.stringify(data));
    }
  }, [selectedAsset]);

  return (
    <StyledBox>
      <StyledTypography>{t('asset_selector')}</StyledTypography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: '',
          margin: '10px 0',
        }}
      >
        <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <StyledTypography
              variant="bodyBold"
              display="block"
              sx={{
                marginBottom: '10px',
              }}
            >
              {t('make_model')}
            </StyledTypography>
            <Selector
              selectorOptions={makeModelList}
              value={selectedMakeModel}
              onChange={(e: any) => {
                dispatch(setSelectedMakeModel(e.target.value));
              }}
              enableDarkTheme={enableDarkTheme}
              minWidth="100%"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <StyledTypography
              variant="bodyBold"
              display="block"
              sx={{
                marginBottom: '10px',
              }}
            >
              {t('bumper_id_11')}
            </StyledTypography>
            <Selector
              selectorOptions={assetList}
              value={selectedAsset?.bumperNumber}
              onChange={(e: any) => {
                handleSelectAsset(e.target.value);
              }}
              enableDarkTheme={enableDarkTheme}
              minWidth="100%"
            />
          </Box>
        </Box>
      </Box>
    </StyledBox>
  );
};

export default AssetSelector;
